import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedNotificationService } from '@luggagehero/shared/services/notification';
import { SharedTranslateService } from '@luggagehero/shared/services/translation';
import { SharedUserService } from '@luggagehero/shared/services/users';
import { SharedUtilPassword } from '@luggagehero/shared/util';
import { TranslateModule } from '@ngx-translate/core';
import { submitNewPassword } from 'supertokens-web-js/recipe/emailpassword';

interface ChangePasswordForm {
  password: FormControl<string>;
  confirmPassword: FormControl<string>;
}

const isSuperTokensGeneralError = (err: unknown): err is { isSuperTokensGeneralError: true; message: string } => {
  return typeof err === 'object' && 'isSuperTokensGeneralError' in err && err.isSuperTokensGeneralError === true;
};

@Component({
  selector: 'lh-shared-feature-reset-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './shared-feature-reset-password.component.html',
  styleUrl: './shared-feature-reset-password.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedFeatureResetPasswordComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private notificationService = inject(SharedNotificationService);
  private translateService = inject(SharedTranslateService);
  private userService = inject(SharedUserService);

  private token: string | null = null;
  private tenantId: string | null = null;

  public form: FormGroup<ChangePasswordForm>;
  public showPassword = false;

  public get canSubmit(): boolean {
    return this.form.valid && this.arePasswordsEqual;
  }

  public get arePasswordsEqual(): boolean {
    return this.form.get('password').value === this.form.get('confirmPassword').value;
  }

  constructor(private fb: FormBuilder) {
    // this.userService.logout(); // this auto redirects so we need to improve it

    this.form = this.fb.group({
      password: [
        '',
        [
          (control: AbstractControl) => Validators.required(control),
          Validators.minLength(8),
          SharedUtilPassword.passwordComplexity(),
        ],
      ],
      confirmPassword: [
        '',
        [
          (control: AbstractControl) => Validators.required(control),
          Validators.minLength(8),
          SharedUtilPassword.passwordComplexity(),
        ],
      ],
    });
  }

  public ngOnInit() {
    this.route.queryParamMap.subscribe((params) => {
      this.token = params.get('token');
      this.tenantId = params.get('tenantId');
    });
  }

  public onSubmit(): void {
    if (this.form.valid) {
      const formData = this.form.value;

      if (formData.password === formData.confirmPassword) {
        // issue change password request
        void this.changePassword(formData.password);
      }
    } else {
      console.error('Form is invalid');
    }
  }

  public async changePassword(password: string) {
    try {
      const response = await submitNewPassword({
        formFields: [
          {
            id: 'password',
            value: password,
          },
        ],
      });

      if (response.status === 'OK') {
        this.notificationService.success('Password updated successfully');
        void this.router.navigate(['home']);
      } else {
        if (response.status === 'FIELD_ERROR') {
          this.notificationService.error(response.formFields[0].error);
        } else {
          this.notificationService.error('Invalid Request');
        }
      }
    } catch (err) {
      if (isSuperTokensGeneralError(err) && err.isSuperTokensGeneralError === true) {
        this.notificationService.error(err.message);
      } else {
        this.notificationService.error(this.translateService.instant('SOMETHING_WENT_WRONG'));
      }
    }
  }
}
